import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { LoaderCircle } from 'lucide-react'
import { useLangPrefix } from '../hooks/useLangPrefix'
import { useAuthStore } from '../store/authStore'
import useManagerStore from '../store/managerStore'
import { submitOnboarding } from '../api/onboarding'
import api from '../lib/api'

// Static fallback — the canonical list is served by GET /public/verticals
// (ai-receptionist app/services/verticals.py) and fetched at mount.
const BUSINESS_TYPES = [
  { value: 'fnb', label: 'Food & Beverage' },
  { value: 'hotel', label: 'Hotel / Resort' },
  { value: 'spa', label: 'Spa / Wellness' },
  { value: 'education', label: 'Education & Training' },
  { value: 'retail', label: 'Retail' },
  { value: 'other', label: 'Other' },
]

function Spinner() {
  return <LoaderCircle className="h-4 w-4 animate-spin" />
}

export default function OnboardingPage() {
  const { prefix } = useLangPrefix()
  const navigate = useNavigate()
  const branches = useAuthStore((s) => s.branches)
  const branchBootstrapStatus = useAuthStore((s) => s.branchBootstrapStatus)
  const tenantId = useManagerStore((s) => s.tenantId)
  const fetchProfile = useAuthStore((s) => s.fetchProfile)
  const [businessName, setBusinessName] = useState('')
  const [businessType, setBusinessType] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [prefilling, setPrefilling] = useState(true)
  const [businessTypes, setBusinessTypes] = useState(BUSINESS_TYPES)

  useEffect(() => {
    let alive = true
    api
      .get('/public/verticals')
      .then((r) => {
        const list = (r?.data?.verticals || [])
          .filter((v) => v?.key)
          .map((v) => ({ value: v.key, label: v.label_en || v.key }))
        if (alive && list.length) setBusinessTypes(list)
      })
      .catch(() => {}) // static fallback stays in place
    return () => {
      alive = false
    }
  }, [])

  const hasKnownBusinessType = useMemo(
    () => businessTypes.some((item) => item.value === businessType),
    [businessType, businessTypes]
  )

  useEffect(() => {
    if (branchBootstrapStatus === 'idle' || branchBootstrapStatus === 'loading') {
      setPrefilling(true)
      return
    }

    const branchList = Array.isArray(branches) ? branches : []
    const activeBranch =
      branchList.find((branch) => branch?.tenant_id === tenantId) ?? branchList[0] ?? null

    const resolvedBusinessName =
      typeof activeBranch?.name === 'string'
        ? activeBranch.name
        : typeof activeBranch?.business_name === 'string'
          ? activeBranch.business_name
          : ''
    const resolvedBusinessType =
      typeof activeBranch?.branch_type === 'string'
        ? activeBranch.branch_type
        : typeof activeBranch?.business_type === 'string'
          ? activeBranch.business_type
          : ''

    setBusinessName(resolvedBusinessName)
    setBusinessType(resolvedBusinessType)
    setPrefilling(false)
  }, [branches, tenantId, branchBootstrapStatus])

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await submitOnboarding({
        business_name: businessName.trim(),
        business_type: businessType,
      })
      await fetchProfile()
      navigate(`${prefix}/dashboard`)
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'submit_onboarding' },
        extra: { tenantId, businessName: businessName.trim(), businessType },
      })
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-100 p-4 text-zinc-900 scheme-light">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-indigo-100/70 via-transparent to-cyan-100/50" />

      <div className="w-full max-w-[480px] relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img
              src="/full_logo.png"
              alt="Growchat"
              className="w-40 h-auto rounded-md object-cover"
            />
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-zinc-900 mb-1">
            Set up your business
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Tell us a bit about your business to get started.
          </p>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                Business name
              </label>
              <input
                type="text"
                className="input"
                placeholder="e.g. Pho Saigon Restaurant"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                disabled={submitting || prefilling}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-zinc-600">
                Business type
              </label>
              <select
                className="input"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                required
                disabled={submitting || prefilling}
              >
                <option value="" disabled>Select a type...</option>
                {businessType && !hasKnownBusinessType ? (
                  <option value={businessType}>{businessType}</option>
                ) : null}
                {businessTypes.map((bt) => (
                  <option key={bt.value} value={bt.value}>{bt.label}</option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={submitting || prefilling}
              className="btn-primary w-full justify-center py-2.5 mt-1 disabled:opacity-50"
            >
              {submitting || prefilling ? (
                <span className="flex items-center gap-2">
                  <Spinner /> {prefilling ? 'Loading business...' : 'Setting up...'}
                </span>
              ) : (
                'Get started →'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
